import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import Pill from '../components/Pill';
import { organizerEventsService } from '../services/organizerEventsService';
import { DATE_NUMERIC, TIME_SHORT, formatEventDate } from '../utils/dates';

interface BackendEvent {
    id: string;
    name: string;
    startDate: string;
    location: string;
    // FINISHED once the end date passes (set by ms-events-management)
    status: 'ACTIVE' | 'DRAFT' | 'FINISHED' | 'CANCELLED' | 'SOLD_OUT';
    imageUrl: string;
    referencePriceFrom: number;
    category: string;
    stages:Array<{
        totalQuantity: number;
    }>;
}

export default function OrganizerEventsView() {
    const navigate = useNavigate();

    const [events, setEvents] = useState<BackendEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'ACTIVE' | 'DRAFT' | 'FINISHED'>('all');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchEvents = async () => {
            setIsLoading(true);
            try {
                const data = await organizerEventsService.getMyEvents();
                setEvents(data.content || []);
            } catch (error) {
                console.error("Error cargando eventos:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEvents();
    }, []);

    const filteredEvents = events.filter(event => {
        const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filter === 'all' || event.status === filter;
        return matchesSearch && matchesStatus;
    });

    // The event's wall time, shown as the organizer typed it: the day, and the time below it
    const formatDay = (dateString: string) => formatEventDate(dateString, DATE_NUMERIC, 'Fecha por definir');
    const formatTime = (dateString: string) => formatEventDate(dateString, TIME_SHORT);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(amount);
    };

    const getStatusBadge = (status: string) => {
        switch(status) {
            case 'ACTIVE': return <span className="st-badge st-badge-success st-badge-dot">Activo</span>;
            case 'DRAFT': return <span className="st-badge st-badge-neutral st-badge-dot">Borrador</span>;
            case 'FINISHED': return <span className="st-badge st-badge-neutral">Finalizado</span>;
            case 'CANCELLED': return <span className="st-badge st-badge-error">Cancelado</span>;
            case 'SOLD_OUT': return <span className="st-badge st-badge-error">Agotado</span>;
            default: return <span className="st-badge st-badge-neutral">{status}</span>;
        }
    };

    // Calcular capacidad total sumando las etapas
    const calculateTotalCapacity = (stages: BackendEvent['stages']) => {
        return stages ? stages.reduce((acc, stage) => acc + stage.totalQuantity, 0) : 0;
    };

    return (
        <div className="st-page py-12">
            <div className="st-container">

                {/* HEADER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">Mis Eventos</h1>
                        <p className="text-[var(--st-text-secondary)]">Gestiona y monitorea el rendimiento de tus eventos.</p>
                    </div>
                    <Button
                        variant="primary"
                        className="st-btn st-btn-primary st-btn-md"
                        onClick={() => navigate('/organizer/events/create')}
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>}
                    >
                        Crear Nuevo Evento
                    </Button>
                </div>

                {/* FILTROS */}
                <div className="st-card-static p-4 mb-6 flex flex-col md:flex-row items-center gap-4">
                    <div className="w-full md:w-80">
                        <Input
                            placeholder="Buscar evento por nombre..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
                        <div onClick={() => setFilter('all')}><Pill active={filter === 'all'}>Todos</Pill></div>
                        <div onClick={() => setFilter('ACTIVE')}><Pill active={filter === 'ACTIVE'}>Activos</Pill></div>
                        <div onClick={() => setFilter('DRAFT')}><Pill active={filter === 'DRAFT'}>Borradores</Pill></div>
                        <div onClick={() => setFilter('FINISHED')}><Pill active={filter === 'FINISHED'}>Finalizados</Pill></div>
                    </div>
                </div>

                {/* TABLA */}
                <div className="st-card-static overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                            <tr className="bg-[var(--st-bg-tertiary)] border-b border-[var(--st-border)] text-xs uppercase text-[var(--st-text-muted)] font-semibold">
                                {/* Long texts wrap instead of widening the table: the actions stay in sight */}
                                <th className="px-3 py-4">Evento</th>
                                <th className="px-3 py-4">Fecha & Lugar</th>
                                <th className="px-3 py-4">Capacidad / Precio Base</th>
                                <th className="px-3 py-4">Estado</th>
                                <th className="px-3 py-4 text-right whitespace-nowrap">Acciones</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--st-border)]">

                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="p-12 text-center">
                                        <div className="flex justify-center"><div className="st-spinner st-spinner-lg"></div></div>
                                    </td>
                                </tr>
                            ) : filteredEvents.length > 0 ? (
                                filteredEvents.map((event) => {
                                    const totalCapacity = calculateTotalCapacity(event.stages);

                                    return (
                                        <tr key={event.id} className="hover:bg-[var(--st-bg-tertiary)] transition-colors group">

                                            {/* INFO BÁSICA */}
                                            <td className="px-3 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0 border border-[var(--st-border)] relative">
                                                        {event.imageUrl ? (
                                                            <img src={event.imageUrl} alt={event.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-[140px] max-w-[220px]">
                                                        <p className="font-bold text-[var(--st-text-primary)] break-words">{event.name}</p>
                                                        <p className="text-xs text-[var(--st-text-muted)] truncate">ID: ...{event.id.slice(-8)}</p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* FECHA Y LUGAR */}
                                            <td className="px-3 py-4">
                                                <div className="space-y-1 max-w-[200px]">
                                                    <div className="flex items-start gap-2 text-sm text-[var(--st-text-secondary)]">
                                                        <svg className="w-4 h-4 mt-0.5 text-[var(--st-text-muted)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                        <div className="whitespace-nowrap">
                                                            <p>{formatDay(event.startDate)}</p>
                                                            {formatTime(event.startDate) && <p>{formatTime(event.startDate)}</p>}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-start gap-2 text-sm text-[var(--st-text-secondary)]">
                                                        <svg className="w-4 h-4 mt-0.5 text-[var(--st-text-muted)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                        <span className="min-w-0 break-words">{event.location}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* DATOS DE VENTA */}
                                            <td className="px-3 py-4">
                                                <div className="max-w-[200px] whitespace-nowrap">
                                                    <div className="flex justify-between gap-2 text-xs mb-1">
                                                        <span className="font-medium text-[var(--st-text-muted)]">Aforo Total:</span>
                                                        <span className="font-bold">{totalCapacity} pax</span>
                                                    </div>
                                                    <p className="text-xs text-[var(--st-text-muted)] mt-1 font-medium">
                                                        Desde: <span className="text-[var(--st-success)]">{formatCurrency(event.referencePriceFrom)}</span>
                                                    </p>
                                                </div>
                                            </td>

                                            {/* ESTADO */}
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                {getStatusBadge(event.status)}
                                            </td>

                                            {/* ACCIONES */}
                                            <td className="px-3 py-4 text-right whitespace-nowrap">
                                                <div className="flex items-center justify-end gap-2">

                                                    {/* BOTÓN EDITAR */}
                                                    <button
                                                        className="p-2 text-[var(--st-text-secondary)] hover:bg-[var(--st-bg-tertiary)] rounded-lg transition-colors"
                                                        title="Editar Evento"
                                                        onClick={() => navigate(`/organizer/events/edit/${event.id}`)} // <--- Navega al formulario con ID
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                    </button>

                                                    {/* BOTÓN CONTROL DE ACCESO (eventos publicados) */}
                                                    {event.status !== 'DRAFT' && event.status !== 'CANCELLED' && (
                                                        <button
                                                            className="p-2 text-[var(--st-text-secondary)] hover:bg-[var(--st-bg-tertiary)] rounded-lg transition-colors"
                                                            title="Control de Acceso"
                                                            onClick={() => navigate(`/organizer/access/${event.id}`)}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                                                        </button>
                                                    )}

                                                    {/* BOTÓN VER (Página Pública) */}
                                                    {/* Usamos target="_blank" para abrir en nueva pestaña */}
                                                    <Link
                                                        to={`/events/${event.id}`}
                                                        target="_blank"
                                                        className="p-2 text-[var(--st-text-secondary)] hover:bg-[var(--st-bg-tertiary)] rounded-lg transition-colors"
                                                        title="Ver Página Pública"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                    </Link>
                                                </div>
                                            </td>

                                        </tr>
                                    );
                                })
                            ) : (
                                // EMPTY STATE
                                <tr>
                                    <td colSpan={5} className="p-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-16 h-16 bg-[var(--st-bg-tertiary)] rounded-full flex items-center justify-center mb-4 text-[var(--st-text-muted)]">
                                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                                            </div>
                                            <h3 className="font-bold text-lg text-[var(--st-text-primary)]">No se encontraron eventos</h3>
                                            <p className="text-[var(--st-text-secondary)] mb-4">
                                                {searchTerm || filter !== 'all'
                                                    ? 'Intenta ajustar tus filtros de búsqueda.'
                                                    : 'Aún no has creado ningún evento.'}
                                            </p>
                                            {filter === 'all' && !searchTerm && (
                                                <Button variant="secondary" size="sm" onClick={() => navigate('/organizer/events/create')}>
                                                    Crear mi primer evento
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}

                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}